import { type AnyRoute, createRoute } from "@tanstack/react-router";
import { ChatDetailPage } from "../components/ChatDetailPage";
import { ChatLayout } from "../components/ChatLayout";
import { ChatPage } from "../components/ChatPage";
import { SharedConversationPage } from "../components/SharedConversationPage";

/**
 * Chat routes composed into the app shell. A pathless layout route mounts the
 * run controller around both the welcome (`/chat`) and thread (`/chat/$id`)
 * routes so streaming state persists across that navigation.
 */
export function createChatRoutes<TParent extends AnyRoute>(parent: TParent) {
  const layout = createRoute({
    getParentRoute: () => parent,
    id: "chat-layout",
    component: ChatLayout,
  });
  const index = createRoute({
    getParentRoute: () => layout,
    path: "/chat",
    component: ChatPage,
    // `?assistant=<id>` scopes a new chat to an assistant (set from its detail
    // page); the welcome hero + first run pick it up. `?skill=<id>` pre-selects
    // a skill in the composer picker (set from the skill detail's "Use in chat").
    validateSearch: (
      search: Record<string, unknown>,
    ): { assistant?: string; skill?: string } => {
      const assistant = search.assistant;
      const skill = search.skill;
      return {
        ...(typeof assistant === "string" && assistant ? { assistant } : {}),
        ...(typeof skill === "string" && skill ? { skill } : {}),
      };
    },
  });
  const detail = createRoute({
    getParentRoute: () => layout,
    path: "/chat/$id",
    component: ChatDetailPage,
  });
  return [layout.addChildren([index, detail])];
}

/**
 * Public shared-conversation route — full-screen and composed directly under the
 * root (outside the auth shell), so it renders for signed-out visitors.
 */
export function createSharedConversationRoute<TParent extends AnyRoute>(parent: TParent) {
  return createRoute({
    getParentRoute: () => parent,
    path: "/share/$token",
    component: SharedConversationPage,
  });
}
